import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Search, RefreshCw, Plus, FileText, Loader2, ChevronRight,
  Building2, Zap, Smartphone, Car, Receipt, Hammer, HeartPulse,
  Anchor, Home, MonitorSmartphone, MoreHorizontal, Landmark,
} from 'lucide-react';

import BillsDueHub from '../components/BillsDueHub';
import PayBillModal from '../components/PayBillModal';

/**
 * Bills Center Workspace — the single screen the user opens to see
 * EVERY payable in the company (supplier · utility · government ·
 * telecom · insurance · customs · rent · …).
 *
 * Layout:
 *   1) Bills Due Hub (4 KPI cards — overdue / week / month / total)
 *   2) Tab strip per bill_kind (with counts)
 *   3) Search + Refresh + New Bill
 *   4) Bills table with Pay button per row → PayBillModal
 *
 * Owns no maths — every number comes from the backend.
 */
const KIND_META = {
  all:          { en: 'All',          ar: 'الكُل',           icon: FileText,       tone: 'slate' },
  supplier:     { en: 'Suppliers',    ar: 'مَوَرِّدين',       icon: Building2,      tone: 'indigo' },
  // Wave P-B.8.4: unified Government & Utility bucket (STC/SEC/NWC/
  // SADAD/GOSI/ZATCA…). All new documents from the "Government &
  // Utility Payments" page carry ``bill_kind='gov_utility'``.
  gov_utility:  { en: 'Gov & Utility',ar: 'حُكومي / مَرافِق',  icon: Landmark,       tone: 'purple' },
  utility:      { en: 'Utilities',    ar: 'مَرافِق',         icon: Zap,            tone: 'amber' },
  telecom:      { en: 'Telecom',      ar: 'اتِّصالات',       icon: Smartphone,     tone: 'indigo' },
  government:   { en: 'Government',   ar: 'حُكومي',          icon: Receipt,        tone: 'emerald' },
  municipality: { en: 'Municipality', ar: 'بَلَدِيَّة',       icon: Building2,      tone: 'teal' },
  traffic_fine: { en: 'Traffic',      ar: 'مُرور',           icon: Car,            tone: 'red' },
  customs:      { en: 'Customs',      ar: 'جَمارِك',         icon: Anchor,         tone: 'blue' },
  insurance:    { en: 'Insurance',    ar: 'تَأمين',          icon: HeartPulse,     tone: 'red' },
  subscription: { en: 'Subscriptions',ar: 'اشتِراكات',       icon: MonitorSmartphone,tone: 'indigo'},
  rent:         { en: 'Rent',         ar: 'إيجار',           icon: Home,           tone: 'amber' },
  professional: { en: 'Professional', ar: 'مِهَنية',         icon: Hammer,         tone: 'purple' },
  other:        { en: 'Other',        ar: 'أُخرى',           icon: MoreHorizontal, tone: 'slate' },
};

const STATUS_BADGE = {
  draft:    'bg-slate-100 text-slate-700',
  received: 'bg-blue-100 text-blue-800',
  approved: 'bg-indigo-100 text-indigo-800',
  partial:  'bg-amber-100 text-amber-800',
  paid:     'bg-green-100 text-green-800',
  overdue:  'bg-red-100 text-red-800',
  cancelled:'bg-slate-100 text-slate-500 line-through',
};

const fmt = (v) =>
  Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const isOverdue = (bill) => {
  if (!bill.due_date || !bill.amount_remaining || bill.amount_remaining <= 0) return false;
  return bill.due_date < new Date().toISOString().slice(0, 10);
};

export default function BillsCenter({ user: _user }) {
  const [hub, setHub] = useState(null);
  const [hubLoading, setHubLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [dueFilter, setDueFilter] = useState(null); // overdue | due_this_week | due_this_month | all
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payingBill, setPayingBill] = useState(null);

  // ── data ────────────────────────────────────────────────────
  const loadHub = useCallback(async () => {
    try {
      setHubLoading(true);
      const { data } = await axiosInstance.get('/bills/due-hub');
      setHub(data);
    } catch {
      // hub is best-effort
    } finally {
      setHubLoading(false);
    }
  }, []);

  const loadBills = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.bill_kind = activeTab;
      if (dueFilter === 'overdue' || dueFilter === 'due_this_week' || dueFilter === 'due_this_month') {
        params.only_due = true;
      } else if (dueFilter === 'all') {
        params.only_due = true;
      }
      const { data } = await axiosInstance.get('/bills', { params });
      let arr = Array.isArray(data) ? data : (data.items || []);
      // Client-side narrowing for week / month buckets so we don't ship
      // a new backend filter — the hub already aggregates these counts.
      const today = new Date().toISOString().slice(0, 10);
      const cutoff = (days) => {
        const d = new Date(); d.setDate(d.getDate() + days);
        return d.toISOString().slice(0, 10);
      };
      if (dueFilter === 'overdue') {
        arr = arr.filter((b) => b.due_date && b.due_date < today && (b.amount_remaining ?? 0) > 0);
      } else if (dueFilter === 'due_this_week') {
        const w = cutoff(7);
        arr = arr.filter((b) => b.due_date && b.due_date >= today && b.due_date <= w);
      } else if (dueFilter === 'due_this_month') {
        const m = cutoff(30);
        arr = arr.filter((b) => b.due_date && b.due_date >= today && b.due_date <= m);
      }
      if (search) {
        const s = search.toLowerCase();
        arr = arr.filter((b) =>
          (b.bill_number || '').toLowerCase().includes(s) ||
          (b.supplier_name || '').toLowerCase().includes(s) ||
          (b.expense_category_name || '').toLowerCase().includes(s),
        );
      }
      setBills(arr);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تَعَذَّر جَلب الفَواتير');
    } finally {
      setLoading(false);
    }
  }, [activeTab, search, dueFilter]);

  useEffect(() => { loadHub(); }, [loadHub]);
  useEffect(() => { loadBills(); }, [loadBills]);

  // ── derived counts per tab ─────────────────────────────────
  const tabCounts = useMemo(() => {
    const out = { all: 0 };
    (hub?.by_kind || []).forEach((k) => { out[k.bill_kind] = k.count; out.all += k.count; });
    return out;
  }, [hub]);

  // ── handlers ───────────────────────────────────────────────
  const onPay = (bill) => setPayingBill(bill);
  const onPaid = () => {
    setPayingBill(null);
    loadBills(); loadHub();
    toast.success('تَم الدَّفع — تَحديث القائِمة');
  };

  // ── render ─────────────────────────────────────────────────
  const tabOrder = ['all','supplier','utility','telecom','government','municipality',
                    'traffic_fine','customs','insurance','rent','subscription','professional','other'];

  return (
    <div className="p-6 space-y-5" data-testid="bills-center-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-indigo-600" />
            مَركَز الفَواتير المُوَحَّد
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            كُل الالتِزامات المالية في شاشَة واحِدَة — مَوَرِّدين، مَرافِق، حُكومي، إيجارات، تَأمينات، وأَكثَر.
          </p>
        </div>
        <button
          onClick={() => { window.location.href = '/supplier-bills?new=1'; }}
          data-testid="bc-new-bill-btn"
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> فاتورة جَديدَة
        </button>
      </div>

      {hubLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin me-2" />
          تَحميل المُؤَشِّرات...
        </div>
      ) : (
        <BillsDueHub data={hub} onFilter={(f) => setDueFilter(f === dueFilter ? null : f)} activeFilter={dueFilter} />
      )}

      {/* Tab strip per bill_kind */}
      <div className="bg-white rounded-xl border border-slate-200 p-2 flex items-center gap-1 overflow-x-auto" data-testid="bc-tabs">
        {tabOrder.map((k) => {
          const meta = KIND_META[k];
          const Icon = meta.icon;
          const cnt = tabCounts[k] ?? 0;
          const isActive = activeTab === k;
          return (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              data-testid={`bc-tab-${k}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 whitespace-nowrap transition-all ${
                isActive
                  ? `bg-${meta.tone}-100 text-${meta.tone}-800 ring-1 ring-${meta.tone}-300`
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {meta.ar}
              {cnt > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive ? `bg-${meta.tone}-200 text-${meta.tone}-900` : 'bg-slate-200 text-slate-700'
                }`}>{cnt}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200 flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="bc-search-input"
            placeholder="بَحث بِرَقم الفاتورة، اسم المُورِّد، أَو التَّصنيف..."
            className="bg-transparent border-0 outline-none text-sm flex-1"
          />
        </div>
        <button
          onClick={() => { loadBills(); loadHub(); }}
          data-testid="bc-refresh-btn"
          className="px-3 py-1.5 rounded-lg text-xs bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          تَحديث
        </button>
        {dueFilter && (
          <button
            onClick={() => setDueFilter(null)}
            className="px-3 py-1.5 rounded-lg text-xs bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
          >
            مَسح فِلتر "{dueFilter}"
          </button>
        )}
      </div>

      {/* Bills Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bc-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="p-2 text-start">النَّوع</th>
                <th className="p-2 text-start">رَقم</th>
                <th className="p-2 text-start">الجِهَة</th>
                <th className="p-2 text-start">التَّصنيف</th>
                <th className="p-2 text-start">تاريخ الاستِحقاق</th>
                <th className="p-2 text-end">المَبلَغ</th>
                <th className="p-2 text-end">المُتَبَقّي</th>
                <th className="p-2 text-center">الحالة</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="p-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline me-2" />
                  جارٍ التَّحميل...
                </td></tr>
              ) : bills.length === 0 ? (
                <tr><td colSpan="9" className="p-8 text-center text-slate-500">
                  <FileText className="w-8 h-8 inline mb-2 opacity-30" />
                  <br />لا تُوجَد فَواتير مُطابِقَة.
                </td></tr>
              ) : bills.map((b) => {
                const kind = b.bill_kind || 'supplier';
                const meta = KIND_META[kind] || KIND_META.other;
                const Icon = meta.icon;
                const overdue = isOverdue(b);
                const remaining = b.amount_remaining ?? 0;
                return (
                  <tr
                    key={b.id}
                    data-testid={`bc-row-${b.id}`}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${overdue ? 'bg-red-50/30' : ''}`}
                  >
                    <td className="p-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-${meta.tone}-100 text-${meta.tone}-800`}>
                        <Icon className="w-3 h-3" />
                        {meta.ar}
                      </span>
                    </td>
                    <td className="p-2 font-mono text-xs text-slate-900">{b.bill_number}</td>
                    <td className="p-2 text-xs text-slate-700">{b.supplier_name || '—'}</td>
                    <td className="p-2 text-xs text-slate-500">
                      {b.expense_category_name || '—'}
                    </td>
                    <td className={`p-2 text-xs ${overdue ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>
                      {b.due_date || '—'}
                    </td>
                    <td className="p-2 text-end font-mono text-xs">{fmt(b.total)}</td>
                    <td className={`p-2 text-end font-mono text-xs font-semibold ${remaining > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      {fmt(remaining)}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_BADGE[b.status] || 'bg-slate-100 text-slate-700'}`}>
                        {b.status || 'draft'}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      {remaining > 0 ? (
                        <button
                          onClick={() => onPay(b)}
                          data-testid={`bc-pay-${b.id}`}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold inline-flex items-center gap-0.5"
                        >
                          دَفع <ChevronRight className="w-3 h-3" />
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {payingBill && (
        <PayBillModal
          bill={payingBill}
          onClose={() => setPayingBill(null)}
          onPaid={onPaid}
        />
      )}
    </div>
  );
}
